import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Sparkle, Microphone, FilmSlate, Translate } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grain min-h-screen flex bg-[#050505] text-white">
      {/* Left cinematic panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 relative p-12 overflow-hidden">
        <div className="absolute inset-0 opacity-40"
          style={{ background: "radial-gradient(circle at 30% 30%, rgba(0,229,255,0.25), transparent 55%), radial-gradient(circle at 70% 70%, rgba(255,0,127,0.22), transparent 55%)" }} />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-magic flex items-center justify-center font-display font-black text-black text-xl">M</div>
          <div>
            <div className="font-display font-extrabold text-xl leading-none">MY CHOICE</div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-400">Studios</div>
          </div>
        </div>
        <div className="relative z-10">
          <h1 className="font-display text-5xl font-black leading-[1.05] max-w-md">
            The operating system for your <span className="text-magic">entertainment company</span>.
          </h1>
          <p className="mt-5 text-zinc-400 max-w-md">
            Create, direct, localize, publish and analyse animation, video and games — multiple studios, controlled by text and voice, from one place.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {[["Ask Studio AI", Sparkle], ["Voice commands", Microphone], ["Production pipeline", FilmSlate], ["Multilingual", Translate]].map(([l, I]) => (
              <span key={l} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-zinc-300">
                <I size={16} weight="duotone" className="text-[#00E5FF]" />{l}
              </span>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-zinc-600">Phase 1 build · Premium studio interface</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-xl bg-magic flex items-center justify-center font-display font-black text-black text-xl">M</div>
            <span className="font-display font-extrabold text-xl">MY CHOICE STUDIOS</span>
          </div>
          <h2 className="font-display text-3xl font-extrabold">Welcome back</h2>
          <p className="text-zinc-400 mt-1.5 mb-8">Sign in to your studio command centre.</p>

          <label className="block text-sm text-zinc-400 mb-1.5">Email</label>
          <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full mb-4 rounded-xl bg-[#121212] border border-white/10 px-4 py-3 text-white outline-none focus:border-[#00E5FF]/50 transition-colors" />

          <label className="block text-sm text-zinc-400 mb-1.5">Password</label>
          <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full mb-2 rounded-xl bg-[#121212] border border-white/10 px-4 py-3 text-white outline-none focus:border-[#00E5FF]/50 transition-colors" />

          {error && <p data-testid="login-error" className="text-sm text-[#FF3366] mb-2">{error}</p>}

          <button data-testid="login-submit" type="submit" disabled={busy}
            className="mt-4 w-full rounded-xl bg-magic py-3 font-semibold text-black hover:opacity-90 transition-opacity disabled:opacity-60">
            {busy ? "Signing in…" : "Enter the Studio"}
          </button>
        </form>
      </div>
    </div>
  );
}
